import { gql } from "@apollo/client";
import _ from "lodash";
import {
  AppWindowIcon,
  BuildingIcon,
  CreditCardIcon,
  PaletteIcon,
  PilcrowIcon,
  StarIcon,
  TypeIcon,
} from "lucide-react";

import {
  DocumentTemplateFontEnum,
  DocumentTemplateFragment,
  DocumentTemplateLayoutEnum,
  TagColorEnum,
} from "@/__generated__/graphql";
import { BoxForEntity } from "@/components/atoms/BoxForEntity";
import { TagColors, Values } from "@/constants/values";
import { icons, iconsByTypename } from "@/objects/@icons";
import { ObjectCardProps, ObjectType } from "@/objects/@types";
import { getReferenceValueOptionsByTypename } from "@/objects";
import { Skeleton } from "@/components/ui/skeleton";
import { Typography } from "@/components/ui/typography";

/** A template as a card: how it lays an invoice out, in what type. */
function DocumentTemplateCard({
  data,
  isLoading = false,
}: ObjectCardProps<DocumentTemplateFragment>) {
  return (
    <div className="flex flex-col gap-1">
      <Typography level="body-sm" className="truncate text-muted-foreground">
        <Skeleton loading={isLoading} variant="inline">
          {[
            _.startCase(_.toLower(data?.layout ?? "")),
            _.startCase(_.toLower(data?.font ?? "")),
            data?.isDefault ? "Default" : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </Skeleton>
      </Typography>
      {data?.paymentInstructions && (
        <Typography
          level="body-sm"
          className="truncate text-muted-foreground/80"
        >
          <Skeleton loading={isLoading} variant="inline">
            {data.paymentInstructions}
          </Skeleton>
        </Typography>
      )}
    </div>
  );
}

const object: ObjectType<DocumentTemplateFragment> = {
  humanName: "template",
  inputKey: "documentTemplate",
  typename: "DocumentTemplateObject",
  ReactIcon: AppWindowIcon,
  titleFieldKey: "name",
  getTitle: (data) => data?.name || "",
  Card: DocumentTemplateCard,
  placeholder: {
    __typename: "DocumentTemplateObject",
    id: btoa("DocumentTemplateObject:placeholder"),
    identifier: "placeholder",
    name: "Standard",
    layout: DocumentTemplateLayoutEnum.Classic,
    font: DocumentTemplateFontEnum.Sans,
    color: TagColorEnum.Blue,
    paymentInstructions: "E-transfer to billing@example.com",
    footer: "",
    isDefault: true,
  } as DocumentTemplateFragment,
  getEntityId: (data) => data?.entity?.id ?? null,
  getReferenceValueOptions: (entityIdentifiers) => ({
    query: gql`
      query DocumentTemplateList($entityIdentifiers: [String!]) {
        documentTemplates(entityIdentifiers: $entityIdentifiers) {
          data {
            ...DocumentTemplate
          }
        }
      }
    `,
    queryOptions: {
      variables: { entityIdentifiers },
    },
    onRenderOption: (option: DocumentTemplateFragment) =>
      option.isDefault ? `${option.name} (default)` : option.name,
    mutationOptions: {
      createNewLabel: "Create new template",
      createTypename: "DocumentTemplateObject",
      entityIdentifier: entityIdentifiers?.[0] ?? null,
    },
  }),
  fieldDefinitions: [
    {
      objectKey: "entity",
      inputKey: "entity",
      label: "",
      type: "custom",
      ReactIcon: BuildingIcon,
      asString: (data) => data?.entity?.identifier,
      getValue: (data) => () => (
        <div
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <BoxForEntity
            identifier={data?.entity?.identifier}
            representation="character"
          />
        </div>
      ),
      fieldProps: {
        exportPath: "entity",
        headerFieldType: "custom",
        isSortable: false,
        isFilterable: false,
        isLockedLeft: true,
        isResizable: false,
        minWidth: Values.DEFAULT_ROW_HEIGHT,
        width: Values.DEFAULT_ROW_HEIGHT,
        maxWidth: Values.DEFAULT_ROW_HEIGHT,
      },
    },
    {
      objectKey: "self",
      inputKey: "self",
      label: "Template",
      type: "reference",
      ReactIcon: iconsByTypename.DocumentTemplateObject,
      asString: (data) => data?.identifier,
      getValue: (data) => ({
        __typename: "DocumentTemplateObject",
        name: data?.name,
        id: data?.id,
        identifier: data?.identifier,
        entityId: data?.entity?.id,
      }),
      getValueOptions: (data) => ({
        isNullable: false,
        ...getReferenceValueOptionsByTypename("DocumentTemplateObject")([
          data?.entity?.identifier,
        ]),
        style: "self",
      }),
      fieldProps: {
        headerFieldType: "custom",
        isSortable: false,
        isFilterable: true,
        isLockedLeft: true,
        isResizable: true,
      },
    },
    {
      objectKey: "name",
      label: "Name",
      type: "text",
      ReactIcon: icons.name,
      asString: (data) => data?.name ?? "",
      getValue: (data) => data?.name,
      createProps: { isRequired: true, isEditable: true },
      updateProps: { isRequired: true, isEditable: true },
      summaryProps: { isVisible: true },
      fieldProps: {
        headerFieldType: "string",
        isSortable: true,
        isFilterable: true,
      },
    },
    {
      // How the invoice is laid out: its title over its details, its heading
      // on a band of its colour, or the same closer together.
      objectKey: "layout",
      inputKey: "layout",
      label: "Layout",
      type: "enum",
      ReactIcon: AppWindowIcon,
      asString: (data) => data?.layout ?? "",
      getValue: (data) => data?.layout,
      getValueOptions: () => ({
        enum: _.map(DocumentTemplateLayoutEnum, (value, key) => ({
          key,
          value,
        })),
      }),
      createProps: { isRequired: false, isEditable: true },
      updateProps: { isRequired: false, isEditable: true },
      summaryProps: { isVisible: true },
      fieldProps: {
        headerFieldType: "enum",
        isSortable: true,
        isFilterable: true,
      },
    },
    {
      // The typeface it is set in, on screen and in its PDF alike.
      objectKey: "font",
      inputKey: "font",
      label: "Font",
      type: "enum",
      ReactIcon: TypeIcon,
      asString: (data) => data?.font ?? "",
      getValue: (data) => data?.font,
      getValueOptions: () => ({
        enum: [
          { key: "Sans serif", value: DocumentTemplateFontEnum.Sans },
          { key: "Serif", value: DocumentTemplateFontEnum.Serif },
          { key: "Monospace", value: DocumentTemplateFontEnum.Mono },
        ],
      }),
      createProps: { isRequired: false, isEditable: true },
      updateProps: { isRequired: false, isEditable: true },
      summaryProps: { isVisible: true },
      fieldProps: {
        headerFieldType: "enum",
        isSortable: true,
        isFilterable: true,
      },
    },
    {
      // Its heading, its rules and what is due are drawn in it.
      objectKey: "color",
      inputKey: "color",
      label: "Colour",
      type: "enum",
      ReactIcon: PaletteIcon,
      asString: (data) => data?.color ?? "",
      getValue: (data) => data?.color,
      getValueOptions: () => ({
        enum: _.map(TagColorEnum, (value, key) => ({ key, value })),
        getColors: (mode) => _.mapValues(TagColors, (colors) => colors[mode]),
      }),
      createProps: { isRequired: false, isEditable: true },
      updateProps: { isRequired: false, isEditable: true },
      summaryProps: { isVisible: true },
      fieldProps: {
        headerFieldType: "enum",
        isSortable: true,
        isFilterable: true,
      },
    },
    {
      // What each new invoice of the business is given.
      objectKey: "isDefault",
      inputKey: "isDefault",
      label: "Default",
      type: "boolean",
      ReactIcon: StarIcon,
      asString: (data) => (data?.isDefault ? "Yes" : "No"),
      getValue: (data) => data?.isDefault,
      createProps: { isRequired: false, isEditable: true },
      updateProps: { isRequired: false, isEditable: true },
      summaryProps: { isVisible: true },
      fieldProps: {
        headerFieldType: "string",
        isSortable: true,
        isFilterable: true,
      },
    },
    {
      // How to pay other than by card: under "Ways to pay" on an invoice
      // that takes e-transfers.
      objectKey: "paymentInstructions",
      inputKey: "paymentInstructions",
      label: "Payment instructions",
      type: "text",
      ReactIcon: CreditCardIcon,
      asString: (data) => data?.paymentInstructions ?? "",
      getValue: (data) => data?.paymentInstructions,
      getValueOptions: () => ({
        placeholder:
          "E-transfer to billing@yourbusiness.ca - auto-deposit is on.",
      }),
      createProps: { isRequired: false, isEditable: true, isFullWidth: true },
      updateProps: { isRequired: false, isEditable: true, isFullWidth: true },
      summaryProps: { isVisible: true },
      fieldProps: {
        headerFieldType: "string",
        isSortable: false,
        isFilterable: true,
      },
    },
    {
      // A line at the foot of every invoice.
      objectKey: "footer",
      inputKey: "footer",
      label: "Footer",
      type: "text",
      ReactIcon: PilcrowIcon,
      asString: (data) => data?.footer ?? "",
      getValue: (data) => data?.footer,
      getValueOptions: () => ({
        placeholder: "Thank you for your business. GST no. 123456789",
      }),
      createProps: { isRequired: false, isEditable: true, isFullWidth: true },
      updateProps: { isRequired: false, isEditable: true, isFullWidth: true },
      summaryProps: { isVisible: true },
      fieldProps: {
        headerFieldType: "string",
        isSortable: false,
        isFilterable: true,
      },
    },
  ],
};

export { object as DocumentTemplateObject };
